import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { CustelOTPInput } from '../../components/ui/CustelOTPInput';
import { CustelPhoneInput } from '../../components/ui/CustelPhoneInput';

/**
 * Sprint 18 Block C T18.14 — Phone Verification standalone sayfa.
 *
 * Style A (auth-card) — LoginPage / VerifyEmailPage / EmailOtpPage ile aynı düzen.
 *
 * Akış (SMS yalnız manuel "Doğrulama kodu gönder" ile — redirect'te otomatik gönderilmez):
 * - STEP 1 — phone input: CustelPhoneInput + "Doğrulama kodu gönder" → POST /auth/phone/start
 * - STEP 2 — OTP input: CustelOTPInput + "Doğrula" + "Kodu tekrar gönder (60sn cooldown)"
 *   → POST /auth/phone/verify
 *
 * Kullanım noktaları:
 * - PHONE_OTP_ENABLED=true iken korumalı endpoint 403 → `/telefon-dogrula` redirect (api.ts)
 * - Profil sayfasından "Telefon ekle/değiştir"
 *
 * JWT-protected: RequireAuth wrapper App.tsx'te.
 */
type Step = 'phone' | 'otp';

export function PhoneVerifyPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const fetchSession = useAuthStore((s) => s.fetchSession);
  const user = useAuthStore((s) => s.user);

  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState<string>('+90');
  const [countryIso, setCountryIso] = useState<string>('TR');
  const [otp, setOtp] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [errorState, setErrorState] = useState<string | null>(null);
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(null);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // Önceden doğrulanmış telefon varsa info banner.
  const alreadyVerified = !!user?.phoneVerifiedAt;

  async function handleStart() {
    setErrorState(null);
    setSubmitting(true);
    try {
      const res = await api.post<{ cooldownSeconds: number; attemptsRemaining: number }>('/auth/phone/start', {
        phone,
      });
      setCooldown(res.cooldownSeconds);
      setAttemptsRemaining(res.attemptsRemaining);
      setStep('otp');
      toast.success(t('phone.codeSent'));
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t('phone.sendFailed');
      setErrorState(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleVerify(code: string) {
    setErrorState(null);
    setSubmitting(true);
    try {
      const res = await api.post<{ emailVerificationRequired?: boolean; email?: string }>('/auth/phone/verify', {
        code,
      });
      toast.success(t('phone.verifySuccess'));
      // Telefon → e-posta zinciri: e-posta doğrulanmamışsa backend zaten TEK kod yolladı;
      // e-posta doğrulama ekranına yönlendir (codeAlreadySent → sayfa mount'ta tekrar göndermez).
      if (res.emailVerificationRequired) {
        navigate('/eposta-dogrula', { state: { email: res.email, codeAlreadySent: true } });
        return;
      }
      await fetchSession();
      navigate('/profil');
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t('phone.verifyFailed');
      setErrorState(message);
      // Backend response'undan attemptsRemaining gelirse güncelle (ConflictException body)
      if (err instanceof ApiError && err.body && typeof err.body === 'object') {
        const body = err.body as { attemptsRemaining?: number };
        if (typeof body.attemptsRemaining === 'number') {
          setAttemptsRemaining(body.attemptsRemaining);
        }
      }
      setOtp('');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-custel-bg-subtle p-8">
      <div className="w-full max-w-md rounded-custel border border-custel-border bg-custel-surface p-8 shadow-sm">
        <h1 className="mb-2 text-2xl font-semibold text-custel-text">
          {step === 'phone' ? t('phone.title') : t('phone.otpTitle')}
        </h1>
        <p className="mb-6 text-sm text-custel-text-secondary">
          {step === 'phone' ? t('phone.subtitle') : t('phone.otpSubtitle', { phone })}
        </p>

        {alreadyVerified && step === 'phone' && (
          <div className="mb-4 rounded-custel border border-custel-success bg-custel-success/10 p-3 text-xs text-custel-text">
            {t('phone.alreadyVerified')}
          </div>
        )}

        {step === 'phone' && (
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-custel-text">{t('phone.numberLabel')}</label>
              <CustelPhoneInput
                value={phone}
                onChange={setPhone}
                countryIso={countryIso}
                onCountryChange={setCountryIso}
                error={!!errorState}
                disabled={submitting}
              />
              {errorState && <p className="mt-1 text-xs text-custel-danger">{errorState}</p>}
            </div>

            <button
              type="button"
              disabled={submitting || phone.length < 10}
              onClick={handleStart}
              className="btn-primary w-full"
            >
              {submitting ? t('phone.sending') : t('phone.sendCode')}
            </button>
          </div>
        )}

        {step === 'otp' && (
          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-custel-text">{t('phone.otpLabel')}</label>
              <CustelOTPInput
                value={otp}
                onChange={setOtp}
                onComplete={handleVerify}
                error={!!errorState}
                disabled={submitting}
                autoFocus
              />
              {errorState && (
                <p className="mt-2 text-xs text-custel-danger">
                  {errorState}
                  {attemptsRemaining != null && (
                    <span className="ml-2 font-mono">
                      {t('phone.attemptsRemaining', { remaining: attemptsRemaining })}
                    </span>
                  )}
                </p>
              )}
            </div>

            <button
              type="button"
              disabled={submitting || otp.length !== 6}
              onClick={() => handleVerify(otp)}
              className="btn-primary w-full"
            >
              {submitting ? t('phone.verifying') : t('phone.verify')}
            </button>

            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={() => {
                  setStep('phone');
                  setOtp('');
                  setErrorState(null);
                }}
                className="text-custel-text-secondary underline-offset-2 hover:text-custel-text hover:underline"
              >
                {t('phone.changePhone')}
              </button>
              <button
                type="button"
                disabled={cooldown > 0 || submitting}
                onClick={handleStart}
                className="text-custel-g700 underline-offset-2 hover:text-custel-ink hover:underline disabled:cursor-not-allowed disabled:text-custel-text-tertiary disabled:no-underline"
              >
                {cooldown > 0 ? t('phone.resendCooldown', { seconds: cooldown }) : t('phone.resendCode')}
              </button>
            </div>
          </div>
        )}

        <p className="mt-6 text-center text-xs text-custel-text-tertiary">
          {t('phone.kvkkNotice')}{' '}
          <a href="/kvkk-aydinlatma" target="_blank" className="underline">
            {t('phone.kvkkLink')}
          </a>
        </p>
      </div>
    </main>
  );
}
